import readline from 'node:readline/promises'

import { TRUTHY } from '../../operations/init/answers'
import { markDirty } from '../output'

/**
 * A yes/no question, for the one thing a command cannot take back: where it is about to write.
 *
 * Returns the default without asking when there is no terminal to ask through — a CI run or an agent must
 * not hang on a closed stdin, and the caller has already decided what the safe answer is there.
 */
export async function confirm(question: string, defaultYes: boolean): Promise<boolean> {
  if (!process.stdin.isTTY) {
    return defaultYes
  }

  const rl = readline.createInterface({ input: process.stdin, output: process.stdout })

  // readline writes the question and echoes the answer itself, invisibly to the output tracker.
  markDirty()

  try {
    const raw = (await rl.question(`${ question } (${ defaultYes ? 'Y/n' : 'y/N' }) `)).trim().toLowerCase()

    return raw === '' ? defaultYes : TRUTHY.includes(raw)
  } catch {
    // Ctrl+D closes stdin and rejects the question. Someone who just closed the input did not say yes, and
    // reporting that as a crash over a directory nobody wrote to would be theatre.
    return false
  } finally {
    rl.close()
  }
}

/**
 * Wait for Enter, for a step with no second option — "now" or Ctrl+C.
 *
 * Deliberately not a `confirm`: a `(Y/n)` suffix invites an answer that would have to be honoured, and the
 * caller here has nothing to do with a No beyond what Ctrl+C already does.
 */
export async function waitForEnter(message: string): Promise<void> {
  if (!process.stdin.isTTY) {
    return
  }

  const rl = readline.createInterface({ input: process.stdin, output: process.stdout })

  markDirty()

  try {
    await rl.question(message)
  } catch {
    // Ctrl+D, same as Ctrl+C for our purposes: stop waiting and let the caller carry on or be interrupted.
  } finally {
    rl.close()
  }
}
